export interface CacheEntry<T> {
    data: T;
    timestamp: number;
    expiresAt: number;
}

export class DataCache {
    private static instance: DataCache;
    private cache: Map<string, CacheEntry<any>> = new Map();
    private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    private constructor() {
        // Load cache from sessionStorage on initialization
        this.loadFromStorage();
        
        // Clean up expired entries periodically
        setInterval(() => this.cleanupExpired(), 60000); // Every minute
    }

    public static getInstance(): DataCache {
        if (!DataCache.instance) {
            DataCache.instance = new DataCache();
        }
        return DataCache.instance;
    }

    public set<T>(key: string, data: T, customDuration?: number): void {
        const duration = customDuration || this.CACHE_DURATION;
        const timestamp = Date.now();
        const expiresAt = timestamp + duration;
        
        const entry: CacheEntry<T> = {
            data,
            timestamp,
            expiresAt
        };
        
        this.cache.set(key, entry);
        this.saveToStorage();
        
    }

    public get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        
        if (!entry) {
            return null;
        }
        
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            this.saveToStorage();
            return null;
        }
        
        return entry.data as T;
    }

    public has(key: string): boolean {
        const entry = this.cache.get(key);
        if (!entry) return false;
        
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            this.saveToStorage();
            return false;
        }
        
        return true;
    }

    public remove(key: string): void {
        this.cache.delete(key);
        this.saveToStorage();
    }

    public clear(): void {
        this.cache.clear();
        this.saveToStorage();
    }

    public getStats(): { totalEntries: number; totalSize: string; entries: Array<{ key: string; size: string; expiresIn: string }> } {
        const entries = Array.from(this.cache.entries()).map(([key, entry]) => {
            const size = new Blob([JSON.stringify(entry.data)]).size;
            const expiresIn = Math.max(0, entry.expiresAt - Date.now());
            
            return {
                key,
                size: this.formatBytes(size),
                expiresIn: this.formatTime(expiresIn)
            };
        });
        
        const totalSize = entries.reduce((sum, entry) => {
            return sum + parseInt(entry.size.replace(/[^\d]/g, ''));
        }, 0);
        
        return {
            totalEntries: entries.length,
            totalSize: this.formatBytes(totalSize),
            entries
        };
    }

    private loadFromStorage(): void {
        try {
            const stored = sessionStorage.getItem('alyze-store-cache');
            if (stored) {
                const parsed = JSON.parse(stored);
                this.cache = new Map(parsed);
                
                // Clean up expired entries immediately after loading
                this.cleanupExpired();                
            }
        } catch (error) {
            console.error('Cache: Error loading from storage:', error);
            this.cache.clear();
        }
    }

    private saveToStorage(): void {
        try {
            const serialized = JSON.stringify(Array.from(this.cache.entries()));
            sessionStorage.setItem('alyze-store-cache', serialized);
        } catch (error) {
            console.error('Cache: Error saving to storage:', error);
            // If storage fails, clear cache to prevent inconsistency
            this.cache.clear();
        }
    }

    private cleanupExpired(): void {
        const now = Date.now();
        let removedCount = 0;
        
        for (const [key, entry] of this.cache.entries()) {
            if (now > entry.expiresAt) {
                this.cache.delete(key);
                removedCount++;
            }
        }
        
        if (removedCount > 0) {
            this.saveToStorage();
        }
    }

    private formatBytes(bytes: number): string {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    private formatTime(milliseconds: number): string {
        const minutes = Math.floor(milliseconds / 60000);
        const hours = Math.floor(minutes / 60);
        
        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        } else if (minutes > 0) {
            return `${minutes}m`;
        } else {
            return `${Math.floor(milliseconds / 1000)}s`;
        }
    }
}

export const dataCache = DataCache.getInstance();
